import { Sentry } from "@/telemetry/sentry"
import { getGPUDebugInfo } from "./gpu"

export const isWindowsEnvironment = () => {
	const os = getOS()
	return os === "Windows"
}

export function getOS() {
	const userAgent = window.navigator.userAgent,
		macosPlatforms = ["macOS", "Macintosh", "MacIntel", "MacPPC", "Mac68K"],
		windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
		iosPlatforms = ["iPhone", "iPad", "iPod"]
	let os: string | null = null

	if (macosPlatforms.some((platform) => userAgent.includes(platform))) {
		os = "Mac OS"
	} else if (iosPlatforms.some((platform) => userAgent.includes(platform))) {
		os = "iOS"
	} else if (windowsPlatforms.some((platform) => userAgent.includes(platform))) {
		os = "Windows"
	} else if (/Android/.test(userAgent)) {
		os = "Android"
	} else if (/Linux/.test(userAgent)) {
		os = "Linux"
	}

	return os
}

export const commandOrCtrl = isWindowsEnvironment() ? "Ctrl" : "⌘"

interface NavigatorUAHighEntropy {
	architecture?: string
}

interface NavigatorUAData {
	getHighEntropyValues?: (hints: string[]) => Promise<NavigatorUAHighEntropy>
}

interface NavigatorWithUAData extends Navigator {
	userAgentData?: NavigatorUAData
}

const hasUserAgentData = (nav: Navigator): nav is NavigatorWithUAData => "userAgentData" in nav

// The userAgent string reports "Intel Mac OS X" on Apple Silicon too, so use it
// only to identify macOS, never to infer architecture.
const isMacUserAgent = (userAgent: string): boolean =>
	/Mac/i.test(userAgent) && !/iPhone|iPad|iPod/i.test(userAgent)

const readWebGLRenderer = (): string | null => {
	if (typeof document === "undefined") {
		return null
	}
	try {
		const { renderer } = getGPUDebugInfo()
		return renderer && renderer !== "unknown" ? renderer : null
	} catch (error) {
		Sentry.addBreadcrumb({
			category: "isMacOnIntel",
			level: "warning",
			message: "getGPUDebugInfo threw — WebGL fallback unavailable",
			data: { error: error instanceof Error ? error.message : String(error) },
		})
		return null
	}
}

/**
 * Detects whether the browser is running on an Intel-based Mac.
 * Returns false when the architecture can't be determined conclusively —
 * false negatives are preferred over false positives to avoid annoying
 * Apple Silicon users with an irrelevant warning.
 */
export const isMacOnIntel = async (): Promise<boolean> => {
	if (typeof navigator === "undefined") {
		return false
	}
	if (!isMacUserAgent(navigator.userAgent)) {
		return false
	}

	const uaData = hasUserAgentData(navigator) ? navigator.userAgentData : undefined
	if (uaData?.getHighEntropyValues) {
		try {
			const { architecture } = await uaData.getHighEntropyValues(["architecture"])
			if (architecture === "x86") return true
			if (architecture === "arm") return false
			Sentry.addBreadcrumb({
				category: "isMacOnIntel",
				level: "info",
				message: "userAgentData returned unknown architecture",
				data: { architecture },
			})
		} catch {
			Sentry.addBreadcrumb({
				category: "isMacOnIntel",
				level: "info",
				message: "getHighEntropyValues rejected — falling back to WebGL",
			})
		}
	}

	const renderer = readWebGLRenderer()
	if (!renderer) {
		return false
	}
	if (/Apple\s*M\d+/i.test(renderer)) {
		return false
	}
	if (/Intel|AMD|Radeon/i.test(renderer)) {
		return true
	}
	return false
}
